/**
 * Local encrypted store.
 *
 * Every content-bearing field is sealed with the vault master key before it
 * touches IndexedDB (`sealed` columns). Rows keep only what an index or the
 * sweeper needs in clear: ids, a blind-indexed peer handle (`peerHash`),
 * timestamps, and coarse status. `expiresAt` drives the 5-minute sweep.
 */
import Dexie, { type EntityTable } from "dexie";
import { DELIVERED_TTL_MS } from "@truewave/protocol";
import { bs } from "../lib/bytes.js";
import { keyvault } from "./keyvault.js";

export type MessageDirection = "in" | "out";
/** Outgoing: queued → sent (relay has it) → delivered (on their device) → read (they opened the chat). */
export type MessageStatus = "queued" | "sent" | "delivered" | "read" | "failed";

/** An attachment's metadata. The bytes live separately — see `attachmentBlobs`. */
export interface AttachmentInfo {
  mimeType: string;
  filename: string;
  size: number;
  /** True once the recipient has opened it — view-once, gone from both sides. */
  viewed: boolean;
  /** A recorded voice message: replayable until the message self-destructs. */
  voice?: boolean;
  durationMs?: number;
  /** Voice notes only: the recipient has played it at least once. */
  heard?: boolean;
}

/** What a reply points at: enough to draw the quote even once the original is gone. */
export interface ReplyRef {
  id: string;
  /** First line or so of the original ("📎 photo.jpg" for an attachment). */
  snippet: string;
  /** User id of whoever wrote the original. */
  authorId: string;
}

/** One reaction emoji per side, like WhatsApp. */
export interface Reactions {
  mine?: string;
  theirs?: string;
}

/** Decrypted view of a message, as the UI consumes it. */
export interface Message {
  id: string;
  peerUserId: string;
  dir: MessageDirection;
  kind: "text" | "attachment";
  text: string;
  attachment?: AttachmentInfo;
  createdAt: number;
  deliveredAt: number | null;
  expiresAt: number | null;
  status: MessageStatus;
  /** False only for an inbound message not yet seen — drives the app-icon badge. */
  read: boolean;
  /** Outgoing: when the recipient's read receipt arrived. */
  readAt?: number | null;
  replyTo?: ReplyRef;
  /** Text was changed after sending. */
  edited?: boolean;
  /** "Deleted for everyone" — shown as a placeholder, content gone. */
  deleted?: boolean;
  reactions?: Reactions;
}

export interface Contact {
  peerUserId: string;
  username: string;
  conversationId: string;
  signaturePublicKey: string;
  verified: boolean;
  addedAt: number;
}

export interface CallLogEntry {
  id: string;
  peerUserId: string;
  dir: MessageDirection;
  startedAt: number;
  endedAt: number;
  outcome: "completed" | "missed" | "declined" | "canceled" | "failed";
  expiresAt: number;
}

interface MetaRow {
  key: string;
  sealed: Uint8Array;
}
interface ContactRow {
  peerHash: string;
  sealed: Uint8Array;
  verified: 0 | 1;
  addedAt: number;
}
interface MessageRow {
  id: string;
  peerHash: string;
  dir: MessageDirection;
  status: MessageStatus;
  createdAt: number;
  deliveredAt: number | null;
  expiresAt: number | null;
  read: 0 | 1;
  readAt?: number | null;
  sealed: Uint8Array;
}
interface CallLogRow {
  id: string;
  peerHash: string;
  endedAt: number;
  expiresAt: number;
  sealed: Uint8Array;
}
interface OutboxRow {
  id: string;
  peerHash: string;
  kind: string;
  sealed: Uint8Array;
  createdAt: number;
  tries: number;
}
/** The decrypted attachment bytes, sealed, kept separate from the (smaller,
 *  more-often-read) message row. Deleted the moment it's viewed. */
interface AttachmentBlobRow {
  messageId: string;
  sealed: Uint8Array;
}

class TrueWaveDB extends Dexie {
  meta!: EntityTable<MetaRow, "key">;
  contacts!: EntityTable<ContactRow, "peerHash">;
  messages!: EntityTable<MessageRow, "id">;
  callLogs!: EntityTable<CallLogRow, "id">;
  outbox!: EntityTable<OutboxRow, "id">;
  attachmentBlobs!: EntityTable<AttachmentBlobRow, "messageId">;

  constructor() {
    super("truewave");
    this.version(1).stores({
      meta: "key",
      contacts: "peerHash, addedAt",
      // [peerHash+createdAt] is no longer queried (see listMessages) but stays
      // declared — dropping an index needs its own version bump and this one is
      // harmless to keep.
      messages: "id, peerHash, expiresAt, createdAt, [peerHash+createdAt]",
      callLogs: "id, peerHash, expiresAt, endedAt",
      outbox: "id, peerHash, createdAt",
    });
    this.version(2).stores({
      attachmentBlobs: "messageId",
    });
    // Adds the `read` index for the app-icon unread badge. Existing rows (from
    // before this feature existed) are backfilled as read — only messages that
    // arrive from here on are tracked, which is fine since they self-delete in
    // 5 minutes anyway.
    this.version(3)
      .stores({
        messages: "id, peerHash, expiresAt, createdAt, [peerHash+createdAt], read",
      })
      .upgrade((tx) =>
        tx
          .table("messages")
          .toCollection()
          .modify((row: MessageRow) => {
            row.read = 1;
          }),
      );
  }
}

export const db = new TrueWaveDB();

// --- meta ----------------------------------------------------------------

export async function putMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, sealed: await keyvault.sealJSON(value) });
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  if (!row) return undefined;
  return keyvault.openJSON<T>(row.sealed);
}

// --- contacts ----------------------------------------------------------------

export async function upsertContact(c: Contact): Promise<void> {
  const { verified, addedAt, ...rest } = c;
  await db.contacts.put({
    peerHash: await keyvault.blindIndex(c.peerUserId),
    sealed: await keyvault.sealJSON({ verified, addedAt, ...rest }),
    verified: verified ? 1 : 0,
    addedAt,
  });
}

export async function listContacts(): Promise<Contact[]> {
  const rows = await db.contacts.orderBy("addedAt").reverse().toArray();
  return Promise.all(rows.map((r) => keyvault.openJSON<Contact>(r.sealed)));
}

export async function getContact(peerUserId: string): Promise<Contact | undefined> {
  const row = await db.contacts.get(await keyvault.blindIndex(peerUserId));
  return row ? keyvault.openJSON<Contact>(row.sealed) : undefined;
}

// --- messages ----------------------------------------------------------------

export async function insertMessage(m: Message): Promise<void> {
  await db.messages.put(await sealMessage(m));
}

export async function getMessage(id: string): Promise<Message | undefined> {
  const row = await db.messages.get(id);
  return row ? openMessage(row) : undefined;
}

export async function updateMessage(
  id: string,
  patch: Partial<Pick<Message, "status" | "deliveredAt" | "expiresAt" | "text" | "edited" | "reactions">>,
): Promise<void> {
  const row = await db.messages.get(id);
  if (!row) return;
  const current = await openMessage(row);
  const next: Message = { ...current, ...patch };
  await db.messages.put(await sealMessage(next));
}

/**
 * "Delete for everyone": keep a placeholder row (so the thread reads "This
 * message was deleted" like WhatsApp, and it still self-destructs on the same
 * timer) but drop every piece of content — text, attachment, reactions, quote.
 */
export async function markMessageDeleted(id: string): Promise<void> {
  const row = await db.messages.get(id);
  if (!row) return;
  const current = await openMessage(row);
  const tombstone: Message = {
    id: current.id,
    peerUserId: current.peerUserId,
    dir: current.dir,
    kind: "text",
    text: "",
    createdAt: current.createdAt,
    deliveredAt: current.deliveredAt,
    expiresAt: current.expiresAt,
    status: current.status,
    read: current.read,
    readAt: current.readAt ?? null,
    deleted: true,
  };
  // Seal first: awaiting Web Crypto inside a Dexie transaction commits it early.
  const sealed = await sealMessage(tombstone);
  await db.transaction("rw", db.messages, db.attachmentBlobs, async () => {
    await db.messages.put(sealed);
    await db.attachmentBlobs.delete(id);
  });
}

/** "Delete for me": gone from this device only. */
export async function deleteMessageLocally(id: string): Promise<void> {
  await db.transaction("rw", db.messages, db.attachmentBlobs, async () => {
    await db.messages.delete(id);
    await db.attachmentBlobs.delete(id);
  });
}

export async function listMessages(peerUserId: string): Promise<Message[]> {
  // Dexie.waitFor is the load-bearing part, not the query shape: this function
  // runs as a Dexie.liveQuery querier (via useMessages/useLiveQuery), and
  // blindIndex's `crypto.subtle.sign` — using the app's real, HKDF-*derived*
  // HMAC key — awaits *before* the first Dexie call. Left unwrapped, that gap
  // is long/foreign enough that Dexie loses track of "a live query is
  // currently being evaluated", so it never notices this query touched
  // `messages` — a write afterwards (a new message arriving) then silently
  // never re-triggers it, even though the write itself succeeds fine. A
  // message arriving while its chat was already open would decrypt correctly
  // but never render until the view was reloaded. Dexie.waitFor tells Dexie to
  // keep that tracking alive across the external await. Root-caused and
  // verified with an isolated Dexie.liveQuery A/B test (bypassing React
  // entirely) in both dev and a production build — a plain generated
  // (non-derived) HMAC key does NOT trigger it, only the real derived one does.
  const peerHash = await Dexie.waitFor(keyvault.blindIndex(peerUserId));
  // A plain single-index equality query rather than the [peerHash+createdAt]
  // compound range it used to be — simpler, and sorting client-side costs
  // nothing here (a conversation never holds more than a few messages, since
  // everything self-deletes within 5 minutes anyway).
  const rows = await db.messages.where("peerHash").equals(peerHash).sortBy("createdAt");
  return Promise.all(rows.map(openMessage));
}

/** Total unread inbound messages across every conversation — the app-icon badge count. */
export async function countUnread(): Promise<number> {
  return db.messages.where("read").equals(0).count();
}

/** Marks every unread inbound message in one conversation as read, returning
 *  the ids that just changed (for read receipts). Operates on the clear
 *  `read`/`dir` columns only, so — unlike most of this file — it never needs to
 *  decrypt (or re-encrypt) anything. */
export async function markConversationRead(peerUserId: string): Promise<string[]> {
  const peerHash = await keyvault.blindIndex(peerUserId);
  const ids: string[] = [];
  await db.messages
    .where("peerHash")
    .equals(peerHash)
    .and((row) => row.dir === "in" && row.read === 0)
    .modify((row) => {
      ids.push(row.id);
      row.read = 1;
    });
  return ids;
}

const STATUS_RANK: Record<MessageStatus, number> = { failed: 0, queued: 1, sent: 2, delivered: 3, read: 4 };

/**
 * Move an outgoing message's status forward — never back. Receipts and the
 * relay's `sent` confirmations can arrive out of order (an outbox resend's
 * `sent` after the `delivered` receipt, a `read` whose `delivered` was lost),
 * and a late one must not turn blue ticks grey again. Reaching delivered or
 * read also starts the self-destruct timer if it hasn't started yet. Clear
 * columns only; nothing is decrypted.
 */
export async function advanceOutgoingStatus(
  id: string,
  status: "sent" | "delivered" | "read",
  now: number = Date.now(),
): Promise<void> {
  await db.transaction("rw", db.messages, async () => {
    const row = await db.messages.get(id);
    if (!row || row.dir !== "out" || STATUS_RANK[row.status] >= STATUS_RANK[status]) return;
    const patch: Partial<MessageRow> = { status };
    if (status === "read") patch.readAt = now;
    if (status !== "sent" && row.deliveredAt === null) {
      patch.deliveredAt = now;
      patch.expiresAt = now + DELIVERED_TTL_MS;
    }
    await db.messages.update(id, patch);
  });
}

async function sealMessage(m: Message): Promise<MessageRow> {
  return {
    id: m.id,
    peerHash: await keyvault.blindIndex(m.peerUserId),
    dir: m.dir,
    status: m.status,
    createdAt: m.createdAt,
    deliveredAt: m.deliveredAt,
    expiresAt: m.expiresAt,
    read: m.read ? 1 : 0,
    readAt: m.readAt ?? null,
    sealed: await keyvault.sealJSON({
      peerUserId: m.peerUserId,
      kind: m.kind,
      text: m.text,
      attachment: m.attachment,
      replyTo: m.replyTo,
      edited: m.edited,
      deleted: m.deleted,
      reactions: m.reactions,
    }),
  };
}

async function openMessage(row: MessageRow): Promise<Message> {
  const body = await keyvault.openJSON<{
    peerUserId: string;
    kind: "text" | "attachment";
    text: string;
    attachment?: AttachmentInfo;
    replyTo?: ReplyRef;
    edited?: boolean;
    deleted?: boolean;
    reactions?: Reactions;
  }>(row.sealed);
  return {
    id: row.id,
    peerUserId: body.peerUserId,
    dir: row.dir,
    kind: body.kind,
    text: body.text,
    ...(body.attachment !== undefined ? { attachment: body.attachment } : {}),
    createdAt: row.createdAt,
    deliveredAt: row.deliveredAt,
    expiresAt: row.expiresAt,
    status: row.status,
    read: row.read === 1,
    readAt: row.readAt ?? null,
    ...(body.replyTo !== undefined ? { replyTo: body.replyTo } : {}),
    ...(body.edited ? { edited: true } : {}),
    ...(body.deleted ? { deleted: true } : {}),
    ...(body.reactions !== undefined ? { reactions: body.reactions } : {}),
  };
}

/** Change an attachment's metadata in place (sealed body, so re-seal). */
export async function patchAttachment(id: string, patch: Partial<AttachmentInfo>): Promise<void> {
  const row = await db.messages.get(id);
  if (!row) return;
  const current = await openMessage(row);
  if (!current.attachment) return;
  const sealed = await sealMessage({ ...current, attachment: { ...current.attachment, ...patch } });
  await db.messages.put(sealed);
}

/** Marks an attachment as opened — the blob itself is deleted separately (see
 *  `deleteAttachmentBlob`), this just updates what the chat thread displays. */
export async function markAttachmentViewed(id: string): Promise<void> {
  const row = await db.messages.get(id);
  if (!row) return;
  const current = await openMessage(row);
  if (!current.attachment) return;
  await db.messages.put(
    await sealMessage({ ...current, attachment: { ...current.attachment, viewed: true } }),
  );
}

// --- attachment blobs ---------------------------------------------------------

export async function insertAttachmentBlob(messageId: string, bytes: Uint8Array): Promise<void> {
  await db.attachmentBlobs.put({ messageId, sealed: await keyvault.seal(bs(bytes)) });
}

export async function getAttachmentBlob(messageId: string): Promise<Uint8Array | undefined> {
  const row = await db.attachmentBlobs.get(messageId);
  return row ? keyvault.open(bs(row.sealed)) : undefined;
}

export async function deleteAttachmentBlob(messageId: string): Promise<void> {
  await db.attachmentBlobs.delete(messageId);
}

// --- call logs ----------------------------------------------------------------

export async function insertCallLog(entry: CallLogEntry): Promise<void> {
  await db.callLogs.put({
    id: entry.id,
    peerHash: await keyvault.blindIndex(entry.peerUserId),
    endedAt: entry.endedAt,
    expiresAt: entry.expiresAt,
    sealed: await keyvault.sealJSON(entry),
  });
}

export async function listCallLogs(): Promise<CallLogEntry[]> {
  const rows = await db.callLogs.orderBy("endedAt").reverse().toArray();
  return Promise.all(rows.map((r) => keyvault.openJSON<CallLogEntry>(r.sealed)));
}

// --- outbox (sends awaiting the relay) --------------------------------------

export interface OutboxItem {
  id: string;
  peerUserId: string;
  kind: string;
  ciphertext: string;
  createdAt: number;
  tries: number;
}

export async function enqueueOutbox(item: OutboxItem): Promise<void> {
  await db.outbox.put({
    id: item.id,
    peerHash: await keyvault.blindIndex(item.peerUserId),
    kind: item.kind,
    createdAt: item.createdAt,
    tries: item.tries,
    sealed: await keyvault.sealJSON(item),
  });
}

export async function listOutbox(): Promise<OutboxItem[]> {
  const rows = await db.outbox.orderBy("createdAt").toArray();
  return Promise.all(rows.map((r) => keyvault.openJSON<OutboxItem>(r.sealed)));
}

export async function dequeueOutbox(id: string): Promise<void> {
  await db.outbox.delete(id);
}

/** Full local wipe — used on sign-out / account reset. */
export async function wipeDatabase(): Promise<void> {
  await db.delete();
  await db.open();
}

// --- clearing content (keeps the account/contacts; just wipes history) -------

/** One contact's messages, call log, and any attachment blobs. */
export async function clearConversation(peerUserId: string): Promise<void> {
  const peerHash = await keyvault.blindIndex(peerUserId);
  const messageIds = await db.messages.where("peerHash").equals(peerHash).primaryKeys();
  await db.transaction("rw", db.messages, db.callLogs, db.attachmentBlobs, async () => {
    await db.messages.where("peerHash").equals(peerHash).delete();
    await db.callLogs.where("peerHash").equals(peerHash).delete();
    await db.attachmentBlobs.bulkDelete(messageIds);
  });
}

/** Every conversation's messages, call logs, and attachment blobs — contacts and the account survive. */
export async function clearAllConversations(): Promise<void> {
  await db.transaction("rw", db.messages, db.callLogs, db.attachmentBlobs, async () => {
    await db.messages.clear();
    await db.callLogs.clear();
    await db.attachmentBlobs.clear();
  });
}

/** Removes a contact and everything exchanged with them. Does not stop them
 *  from messaging you again — see messaging.ts's handleDeliver, which
 *  re-creates a minimal Contact if a known conversation gets a new message. */
export async function deleteContact(peerUserId: string): Promise<void> {
  await clearConversation(peerUserId);
  await db.contacts.delete(await keyvault.blindIndex(peerUserId));
}
